"use client";

import { c as _c } from "react-compiler-runtime";
import { CameraIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Eyebrow from "@/assets/icons/eyebrow.svg";
import Glasses from "@/assets/icons/glasses.svg";
import Sun from "@/assets/icons/sun.svg";
import { Button } from "@/components/ui/button";
import { ResponsiveModal, ResponsiveModalContent, ResponsiveModalTitle } from "@/components/ui/responsive-modal";
import { useTranslation } from "@/core/i18n";
const STAGGER_DELAY = 0.08;
const TRANSITION = {
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1]
} as const;
function getStaggerProps(index: number) {
  return {
    initial: {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      ...TRANSITION,
      delay: index * STAGGER_DELAY
    }
  };
}
type LashBrowPhotoTipsOverlayProps = {
  open: boolean;
  isStarting?: boolean;
  onStart: () => void | Promise<void>;
  onUploadInstead?: () => void;
};
const BEST_RESULT_TIPS = [{
  icon: Eyebrow,
  key: "lashBrowSteps.intake.tips.frontFacing"
}, {
  icon: Glasses,
  key: "lashBrowSteps.intake.tips.eyesOpen"
}, {
  icon: Sun,
  key: "lashBrowSteps.intake.tips.evenLight"
}] as const;
export function LashBrowPhotoTipsOverlay(t0) {
  const $ = _c(11);
  const {
    open,
    isStarting: t1,
    onStart,
    onUploadInstead
  } = t0;
  const isStarting = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[0] !== isStarting || $[1] !== onStart || $[2] !== onUploadInstead || $[3] !== open || $[4] !== t) {
    t2 = open && <><div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden sm:gap-3"><motion.div className="flex flex-col items-center gap-2 text-center sm:mb-2 sm:gap-3" {...getStaggerProps(0)}><div className="bg-foreground-muted flex h-11 w-11 items-center justify-center rounded-full sm:h-14 sm:w-14"><CameraIcon className="h-6 w-6 text-primary [&_*]:stroke-[1.5] [&_*]:[stroke-linecap:round] [&_*]:[stroke-linejoin:round]" /></div><ResponsiveModalTitle asChild={true}><h2 className="type-heading-md text-foreground sm:type-heading-lg font-medium">{t("lashBrowSteps.intake.title")}</h2></ResponsiveModalTitle></motion.div><motion.div {...getStaggerProps(1)}><p className="type-caption text-center">{t("lashBrowSteps.intake.subtitle")}</p></motion.div><hr className="border-foreground/8 my-0" /><motion.div className="space-y-2 sm:space-y-3" {...getStaggerProps(2)}><p className="type-overline text-foreground">{t("assessmentSteps.studioSession.tips.bestResults")}</p><motion.div className="border-foreground/8 bg-foreground-muted/50 divide-foreground/8 divide-y rounded-xl border" {...getStaggerProps(3)}>{BEST_RESULT_TIPS.map(t3 => {
              const {
                icon: TipIcon,
                key
              } = t3;
              return <div className="type-body-sm text-foreground flex items-center gap-2.5 px-3 py-2 sm:gap-3 sm:py-3" key={key}><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-10 sm:w-10"><TipIcon className="h-5 w-5 text-primary [&_*]:stroke-[1.5] [&_*]:[stroke-linecap:round] [&_*]:[stroke-linejoin:round]" /></span><span>{t(key)}</span></div>;
            })}</motion.div></motion.div></div><motion.div className="shrink-0 pt-2 sm:pt-3" {...getStaggerProps(6)}><div className="flex flex-col gap-2 sm:gap-3"><Button className="w-full" disabled={isStarting} onClick={() => {
            onStart();
          }}>{isStarting ? t("assessmentSteps.studioSession.requesting") : t("assessmentSteps.studioSession.tips.start")}</Button>{onUploadInstead && <Button className="h-auto min-h-10 w-full py-2 sm:min-h-0 sm:py-0" disabled={isStarting} type="button" variant="ghost" onClick={onUploadInstead}>{t("assessmentSteps.studioSession.tips.uploadInstead")}</Button>}</div></motion.div></>;
    $[0] = isStarting;
    $[1] = onStart;
    $[2] = onUploadInstead;
    $[3] = open;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <ResponsiveModalContent className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:overflow-y-auto sm:pb-[calc(env(safe-area-inset-bottom)+1.5rem)]" dialogClassName="gap-2 !px-4 !pt-6 !pb-3 sm:gap-4 sm:!px-6 sm:!pt-10 sm:!pb-8" dismissOnOutsideInteract={false}><AnimatePresence>{t2}</AnimatePresence></ResponsiveModalContent>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== open || $[9] !== t3) {
    t4 = <ResponsiveModal mobileVariant="modal" onOpenChange={_temp} open={open}>{t3}</ResponsiveModal>;
    $[8] = open;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp() {}